import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import type { ReactNode } from "react";
import type {
  ChatToolPartPlugin,
  ToolPartPluginRenderArgs,
} from "@/lib/ai/chat-client-plugin";
import { DELETE_INDEX_WORKER_NAME } from "@/lib/ai/tools/delete-index-worker/constants";
import { MOVE_INDEX_WORKER_PROJECT_NAME } from "@/lib/ai/tools/move-index-worker-project/constants";
import {
  getToolApprovalReviewRegistry,
  getToolConfirmRegistry,
  getToolOutputRegistry,
} from "@/lib/ai/tools/ui";
import { IndexWorkerWatchRuleConflictError } from "@/lib/ai/tools/ui/index-worker-watch-rule-conflict-error";
import { ToolApprovalReview } from "@/lib/ai/tools/ui/tool-approval-review";
import type { AgentUIMessage } from "@/lib/ai/types";
import { isIndexWorkerWatchRuleConflict } from "@/lib/metric-watch-errors";
import type { RenderableToolUIPart, ToolRenderPlan } from "./render-model";
import { getRenderableToolOutput } from "./tool-output-presentation";

/** plugin と message 単位の状態を一緒に render plan へ渡すための組。 */
export type MessageToolPartPluginEntry = {
  plugin: ChatToolPartPlugin;
  messageState: unknown;
};

function isRenderableToolUIPart(
  part: AgentUIMessage["parts"][number],
): part is RenderableToolUIPart {
  return isToolUIPart(part as never);
}

/**
 * dynamic tool part を既存 plugin / registry が扱う static tool key へ揃える。
 */
function toStaticToolUIPart(part: RenderableToolUIPart): ToolUIPart {
  if (part.type !== "dynamic-tool") {
    return part;
  }

  return {
    ...part,
    type: `tool-${part.toolName}` as `tool-${string}`,
  };
}

function buildRenderPlanId(
  message: AgentUIMessage,
  part: RenderableToolUIPart,
  partIndex: number,
): string {
  return `${message.id}-tool-${partIndex}-${part.toolCallId ?? part.type}`;
}

function renderWithPlugin(
  entries: MessageToolPartPluginEntry[],
  args: Omit<ToolPartPluginRenderArgs, "messageState">,
):
  | { kind: "hidden" }
  | {
      kind: "render";
      node: ReactNode;
      placement: ChatToolPartPlugin["placement"];
      title: string | undefined;
    }
  | null {
  for (const { plugin, messageState } of entries) {
    const pluginArgs = { ...args, messageState };
    // JSX 内で重複を隠すだけでは Activity 行が残るため、描画モデルを作る前に除外する。
    if (plugin.shouldHidePart?.(pluginArgs)) {
      return { kind: "hidden" };
    }
    const node = plugin.tryRender(pluginArgs);
    if (node !== null) {
      return {
        kind: "render",
        node,
        placement: plugin.placement,
        title: plugin.toolCallTitle,
      };
    }
  }

  return null;
}

/**
 * 各 tool part の描画方法と配置を優先順位どおり一度だけ決定する。
 *
 * plugin → approval / legacy confirmation → 非承認 error → custom output
 * → generic の順序をここへ閉じ込める。approval の専用 output parser が payload を
 * 拒否した場合も、review 情報を残したまま generic 詳細へ退避する。grouping と描画で
 * parser や plugin を再実行しないため、表示場所によって判定が変わることもない。
 */
export function getToolRenderPlans({
  message,
  chatStatus,
  isLastMessage,
  messageToolPartPlugins,
}: {
  message: AgentUIMessage;
  chatStatus: ToolPartPluginRenderArgs["chatStatus"];
  isLastMessage: boolean;
  messageToolPartPlugins: MessageToolPartPluginEntry[];
}): Map<number, ToolRenderPlan> {
  const plans = new Map<number, ToolRenderPlan>();
  const approvalReviewRegistry = getToolApprovalReviewRegistry();
  const confirmRegistry = getToolConfirmRegistry();
  const outputRegistry = getToolOutputRegistry();

  message.parts.forEach((part, partIndex) => {
    if (!isRenderableToolUIPart(part)) {
      return;
    }

    const staticPart = toStaticToolUIPart(part);
    const id = buildRenderPlanId(message, part, partIndex);

    const pluginResult = renderWithPlugin(messageToolPartPlugins, {
      part: staticPart,
      partIndex,
      message,
      chatStatus,
      isLastMessage,
    });

    if (pluginResult?.kind === "hidden") {
      return;
    }

    if (pluginResult?.kind === "render") {
      plans.set(partIndex, {
        id,
        part: staticPart,
        placement: pluginResult.placement ?? "standalone",
        renderer: "plugin",
        title: pluginResult.title,
        node: pluginResult.node,
      });
      return;
    }

    const toolName = getToolName(staticPart);
    const indexWorkerConflictOperation =
      toolName === DELETE_INDEX_WORKER_NAME
        ? "delete"
        : toolName === MOVE_INDEX_WORKER_PROJECT_NAME
          ? "move"
          : null;
    if (
      staticPart.state === "output-error" &&
      indexWorkerConflictOperation &&
      isIndexWorkerWatchRuleConflict(staticPart.errorText)
    ) {
      plans.set(partIndex, {
        id,
        part: staticPart,
        placement: "standalone",
        renderer: "plugin",
        node: (
          <IndexWorkerWatchRuleConflictError
            operation={indexWorkerConflictOperation}
          />
        ),
      });
      return;
    }
    const hasApproval =
      "approval" in staticPart && Boolean(staticPart.approval);
    if (
      (hasApproval &&
        [
          "approval-requested",
          "approval-responded",
          "output-available",
          "output-error",
          "output-denied",
        ].includes(staticPart.state)) ||
      (staticPart.state === "input-available" && confirmRegistry[toolName])
    ) {
      const outputRegistration = outputRegistry[toolName];
      const approvalReviewRegistration = hasApproval
        ? approvalReviewRegistry[toolName]
        : undefined;
      let outputNode: ReactNode;
      let renderer: ToolRenderPlan["renderer"] = "confirmation";
      if (staticPart.state === "output-available" && outputRegistration) {
        const parsedOutput = outputRegistration.parse(
          getRenderableToolOutput(staticPart.output),
        );
        if (parsedOutput !== null) {
          const RegistryOutput = outputRegistration.component;
          outputNode = (
            <RegistryOutput output={parsedOutput} part={staticPart} />
          );
        } else {
          // 登録済み parser が version skew 等で拒否した payload は隠さず、
          // 共通詳細へ退避して診断可能性を維持する。
          renderer = "generic-tool";
        }
      }
      plans.set(partIndex, {
        id,
        part: staticPart,
        placement: [
          "output-available",
          "output-error",
          "output-denied",
        ].includes(staticPart.state)
          ? "activity"
          : "standalone",
        renderer,
        node: outputNode,
        approvalReview: approvalReviewRegistration
          ? {
              node: (
                <ToolApprovalReview
                  component={approvalReviewRegistration.component}
                  part={staticPart}
                  phase={
                    staticPart.state === "approval-requested" ||
                    staticPart.state === "approval-responded"
                      ? "request"
                      : "history"
                  }
                />
              ),
              approveLabel: approvalReviewRegistration.approveLabel,
              denyLabel: approvalReviewRegistration.denyLabel,
              approveVariant: approvalReviewRegistration.approveVariant,
              refreshRouteOnOutput:
                approvalReviewRegistration.refreshRouteOnOutput,
            }
          : undefined,
      });
      return;
    }

    // plugin が失敗 payload の診断表示を提供しない場合だけ、共通の tool 詳細へ集約する。
    if (staticPart.state === "output-error") {
      plans.set(partIndex, {
        id,
        part: staticPart,
        placement: "activity",
        renderer: "generic-tool",
      });
      return;
    }

    const outputRegistration = outputRegistry[toolName];
    if (staticPart.state === "output-available" && outputRegistration) {
      const parsedOutput = outputRegistration.parse(
        getRenderableToolOutput(staticPart.output),
      );
      if (parsedOutput !== null) {
        const RegistryOutput = outputRegistration.component;
        plans.set(partIndex, {
          id,
          part: staticPart,
          placement: "activity-and-standalone",
          renderer: "custom-output",
          node: <RegistryOutput output={parsedOutput} part={staticPart} />,
        });
        return;
      }
    }

    plans.set(partIndex, {
      id,
      part: staticPart,
      placement: "activity",
      renderer: "generic-tool",
    });
  });

  return plans;
}
